import React from 'react'
import { Card, CardBody, IconButton } from '@material-tailwind/react'
import QRCode from 'react-qr-code'
import { PiTrash, PiDownloadSimple } from 'react-icons/pi'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { removeManualCodeLibrarySuffix } from '../utils/helpers/agami.helpers'
import { theme } from '../tailwind.config'
import { downloadPng } from '../utils/helpers/download.helpers'
import AgamiDraftChip from '../components/AgamiDraftChip'
import AgamiMediaStatus from '../components/AgamiMediaStatus'
import { getRouteBaseUrl } from '../utils/helpers/url.helpers'
import { AgamiSpot } from '../types/supabase'
import { useUserLandingPages } from '../hooks/useUserLandingPages'
import { useAgamiRoutes } from '../hooks/useAgamiRoutes'
import { DeleteAgamiDialog } from '../components/DeleteAgamiDialog'
import { AgamiCardDetails } from '../components/AgamiCardDetails'

interface AgamiPreviewCardProps {
  userId: string | null
  spot: AgamiSpot
  removeFromExperience?: () => void
  refetchAgamiList: (() => void) | undefined
  showQrCode?: boolean
}

const AgamiPreviewCard: React.FC<AgamiPreviewCardProps> = ({
  userId,
  spot,
  removeFromExperience,
  refetchAgamiList,
  showQrCode,
}) => {
  const { t } = useTranslation()
  const [openConfirm, setOpenConfirm] = React.useState(false)

  const navigate = useNavigate()
  const { agamiFormPath } = useAgamiRoutes()

  const manageDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (removeFromExperience) {
      removeFromExperience()
    } else {
      setOpenConfirm(true)
    }
  }

  const editSpot = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(agamiFormPath(spot.id))
  }

  const manualCode = spot?.experience_content_spots?.[0]?.manual_code
    ? removeManualCodeLibrarySuffix(spot.experience_content_spots[0].manual_code)
    : undefined

  const qrCodeRef = React.useRef(null)
  const { colors } = theme.extend
  const PRIMARY = colors.primary
  const GRAY = colors['base-gray']
  const qrFramePadding = 12
  const qrFrameColor = GRAY[400]

  const [qrHover, setQrHover] = React.useState(false)

  const handleDownload = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      downloadPng({
        elementRef: qrCodeRef,
        title: manualCode ?? '',
        padding: qrFramePadding,
        frameColor: qrFrameColor,
      })
    },
    [manualCode, qrFrameColor, qrFramePadding]
  )

  const cardBodyClass = spot?.is_draft
    ? 'bg-blue-50 hover:bg-blue-100 dark:bg-accent-blue-900 dark:hover:bg-accent-blue-800'
    : 'hover:bg-base-gray-500 dark:hover:bg-primary-300'

  // Resolve the landing page code to embed in the QR URL: explicit spot
  // association wins; otherwise fall back to the spot owner's default (if any).
  const { landingPages, defaultLandingPage } = useUserLandingPages()
  const landingPageCode = React.useMemo(() => {
    if (spot.landing_page_code_id != null) {
      const explicit = landingPages.find((lp) => lp.id === spot.landing_page_code_id)
      if (explicit) return explicit.code
    }
    return defaultLandingPage?.code ?? null
  }, [spot.landing_page_code_id, landingPages, defaultLandingPage])

  const manualCodeQrUrl = `${getRouteBaseUrl()}/manual-code?agami-manual-code=${manualCode}${
    landingPageCode ? `&landing-page=${encodeURIComponent(landingPageCode)}` : ''
  }`

  return (
    <>
      <DeleteAgamiDialog
        open={openConfirm}
        userId={userId ?? ''}
        agami={{
          id: spot.id,
          name: spot.name,
          mediaPath: spot.audio_content_path,
          mediaType: spot.content_media_type ?? 1,
        }}
        onClose={() => setOpenConfirm(false)}
        onDeleted={refetchAgamiList}
      />

      <div className="flex gap-0 h-full">
        <Card
          className="w-full h-full !items-start cursor-pointer transition-all"
          onClick={editSpot}
          data-testclass="agami-preview-card"
        >
          <CardBody className={`${cardBodyClass} !h-full flex flex-col`}>
            <div className="flex space-x-2 justify-between items-start">
              <AgamiCardDetails spot={spot} nameTestClass="agami-preview-card-name" />

              <div className="flex items-start gap-2">
                <AgamiDraftChip spot={spot} />
                <IconButton
                  variant="text"
                  size="sm"
                  color="yellow"
                  onClick={manageDelete}
                  data-testclass="agami-preview-card-delete-btn"
                >
                  <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
                </IconButton>
              </div>
            </div>
            {/* One shared component for the three media states (07.2). */}
            <AgamiMediaStatus
              spot={spot}
              playerClassName="w-[130%] ml-[-15%] h-6 mt-auto pt-2 scale-75"
              alertClassName="w-full md:w-1/2 py-2 px-4 mt-auto text-xs"
              showIntentionalNone={false}
            />
          </CardBody>
        </Card>
        {spot?.type === 2 && manualCode && showQrCode && (
          <Card className="border-l-2 border-dashed border-base-gray-600 px-2">
            <CardBody
              className="flex flex-col justify-center items-center gap-4 bg-base-gray-400"
              onMouseEnter={() => setQrHover(true)}
              onMouseLeave={() => setQrHover(false)}
            >
              <QRCode
                style={{ transition: 'opacity .3s', opacity: qrHover ? 0.3 : 1 }}
                data-url={manualCodeQrUrl}
                className="w-[100px] h-[100px]"
                fgColor={PRIMARY[500]}
                bgColor={GRAY[400]}
                ref={qrCodeRef}
                value={manualCodeQrUrl}
                title={`Agami QR Code - ${manualCode}`}
              />
              {qrHover && (
                <div className="absolute opacity-100">
                  <IconButton
                    aria-label={t('accessCode.downloadQr')}
                    className="rounded-full bg-primary dark:bg-primary-400 text-accent border-2 border-primary-500"
                    onClick={handleDownload}
                  >
                    <PiDownloadSimple className="h-5 w-5" />
                  </IconButton>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </>
  )
}

export default AgamiPreviewCard
